import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { changePassword, updatePhone } from '../services/api';
import { useLang } from '../context/LanguageContext';

export default function ProfilePage({ user, onBack }) {
  const { t } = useLang();
  const p = t.profile;

  const [oldPassword, setOldPassword]         = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState('');
  const [success, setSuccess]                 = useState(false);
  const [showConfirm, setShowConfirm]         = useState(false);
  const [phone, setPhone]                     = useState(user.phone || '');
  const [phoneSaved, setPhoneSaved]           = useState(false);
  const [phoneLoading, setPhoneLoading]       = useState(false);

  const handlePhoneSave = async () => {
    setPhoneLoading(true);
    try {
      await updatePhone(phone);
      const stored = JSON.parse(localStorage.getItem('taxi-user') || '{}');
      localStorage.setItem('taxi-user', JSON.stringify({ ...stored, phone }));
      setPhoneSaved(true);
      setTimeout(() => setPhoneSaved(false), 3000);
    } catch {
      toast.error(p.phoneError);
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleUpdateClick = () => {
    setError('');
    if (!oldPassword || !newPassword || !confirmPassword) {
      setError(p.errFill);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(p.errMatch);
      return;
    }
    if (newPassword.length < 6) {
      setError(p.errLength);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    setLoading(true);
    setError('');
    try {
      await changePassword({ oldPassword, newPassword, confirmNewPassword: confirmPassword });
      setSuccess(true);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.message || p.errGeneral);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-900 min-h-screen">
    <div className="max-w-lg mx-auto px-6 py-10">

      <button onClick={onBack} className="flex items-center gap-1 text-sm text-white/40 hover:text-white mb-8 transition-colors">
        {p.back}
      </button>

      <h1 className="text-2xl font-black text-white mb-1">{p.title}</h1>
      <p className="text-sm text-white/40 mb-8">{user.name} - {user.role.toLowerCase()}</p>

      {/* ── Account info ── */}
      <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 mb-6">
        <h2 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4">{p.accountSection}</h2>
        <div className="space-y-3">
          <div>
            <p className="text-xs text-white/40 mb-0.5">{p.name}</p>
            <p className="text-sm font-medium text-white">{user.name}</p>
          </div>
          <div>
            <p className="text-xs text-white/40 mb-0.5">{p.email}</p>
            <p className="text-sm font-medium text-white">{user.username}</p>
          </div>
          <div>
            <p className="text-xs text-white/40 mb-0.5">{p.role}</p>
            <p className="text-sm font-medium text-white">{user.role}</p>
          </div>
          <div>
            <p className="text-xs text-white/40 mb-0.5">{p.phone}</p>
            <div className="flex gap-2 mt-1">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={p.phonePlaceholder}
                className="flex-1 bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
              />
              <button
                onClick={handlePhoneSave}
                disabled={phoneLoading}
                className="btn-primary px-4 disabled:opacity-50"
              >
                {phoneSaved ? p.phoneSaved : p.phoneBtn}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Change password ── */}
      <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
        <h2 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4">{p.passwordSection}</h2>

        {success ? (
          <div className="bg-green-500/20 border border-green-500/30 text-green-400 text-sm px-4 py-3 rounded-xl">
            {p.successMsg}
          </div>
        ) : (
          <div className="space-y-3">
            <input
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              placeholder={p.oldPassword}
              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
            />
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={p.newPassword}
              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={p.confirmPassword}
              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
            />

            {error && (
              <div className="bg-red-500/20 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-xl">{error}</div>
            )}

            <button
              onClick={handleUpdateClick}
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-50 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary-600/30"
            >
              {loading ? p.updating : p.updateBtn}
            </button>
          </div>
        )}
      </div>

      {/* ── Confirm modal ── */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-sm bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
            <h2 className="text-lg font-bold text-white mb-2">{p.confirmTitle}</h2>
            <p className="text-sm text-white/50 mb-6">{p.confirmText}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-3 rounded-xl border-2 border-white/20 text-sm font-semibold text-white/50 hover:border-white/40 hover:text-white transition-colors"
              >
                {p.confirmNo}
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 btn-primary justify-center"
              >
                {p.confirmYes}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
